import React, { useState } from 'react';
import { FaUserSlash, FaDownload } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('session_token')}` });

function DeletionDetails({ check }) {
  if (!check) return <p className="text-sm text-gray-500">Checking your account...</p>;
  if (!check.can_delete) {
    return (
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="delete-account-blocked">
        <p className="font-semibold mb-1">Hand over your families first</p>
        <p className="mb-2">You own families that other members still use. Open each one and transfer ownership to another member so their photos, vault and tree stay safe:</p>
        <ul className="list-disc pl-5">{check.families_to_transfer.map(f => <li key={f.family_id}>{f.name}</li>)}</ul>
      </div>
    );
  }
  return (
    <div className="text-sm text-gray-700 space-y-2" data-testid="delete-account-details">
      <p>This permanently removes your profile, private vault documents, notifications and any paid plan. It cannot be undone.</p>
      {check.solo_families_deleted.length > 0 && (
        <p>These families have no other members and will also be deleted: <strong>{check.solo_families_deleted.join(', ')}</strong></p>
      )}
    </div>
  );
}

export function DownloadMyDataButton() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.get('/api/account/export', { headers: authHeaders(), withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `legacy-forge-my-data-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Your photos, documents and data are downloading');
    } catch {
      toast.error('Download failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900 flex items-center justify-between gap-3">
      <span>Keep a copy of the photos and documents you uploaded first.</span>
      <Button variant="outline" size="sm" onClick={download} disabled={busy} data-testid="download-my-data-btn">
        <FaDownload className="mr-1" />{busy ? 'Preparing…' : 'Download'}
      </Button>
    </div>
  );
}

export function DeleteAccountButton() {
  const [open, setOpen] = useState(false);
  const [check, setCheck] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const openDialog = async () => {
    setOpen(true);
    setCheck(null);
    setConfirmText('');
    try {
      const res = await axios.get('/api/account/deletion-check', { headers: authHeaders(), withCredentials: true });
      setCheck(res.data);
    } catch {
      toast.error('Could not check your account. Please try again.');
      setOpen(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await axios.delete('/api/account', { data: { confirm: 'DELETE' }, headers: authHeaders(), withCredentials: true });
      localStorage.removeItem('session_token');
      document.cookie = 'session_token=; path=/; max-age=0; samesite=none; secure';
      toast.success('Your account has been deleted');
      setTimeout(() => window.location.replace('/'), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={openDialog}
        className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 text-gray-600 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors text-xs sm:text-sm"
        data-testid="delete-account-btn"
        title="Delete Account"
      >
        <FaUserSlash />
        <span>Delete Account</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-account-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><FaUserSlash /> Delete your account</DialogTitle>
            <DialogDescription>Family content you shared with others stays with the family.</DialogDescription>
          </DialogHeader>
          <DeletionDetails check={check} />
          {check && <DownloadMyDataButton />}
          {check?.can_delete && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Type DELETE to confirm</label>
              <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="DELETE" data-testid="delete-account-confirm-input" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="delete-account-cancel">Cancel</Button>
            {check?.can_delete && (
              <Button
                onClick={handleDelete}
                disabled={confirmText !== 'DELETE' || submitting}
                className="bg-red-600 hover:bg-red-700"
                data-testid="delete-account-submit"
              >
                {submitting ? 'Deleting...' : 'Delete forever'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
